import { beginLoading, trackedFetch } from "@/lib/loading-activity";

/**
 * Resolve platform API base URL.
 * Order: window.__TINYBASE_URL__ (runtime) → VITE_TINYBASE_URL (build) → default.
 * Prefer 127.0.0.1 over localhost: on Windows, localhost often resolves to ::1
 * first while Docker Desktop only publishes IPv4, causing multi-second delays.
 *
 * Use `same-origin`, `/`, or empty string to call the current page origin
 * (Vite dev/preview proxy or nginx reverse-proxy). That keeps portal CSRF
 * cookies first-party on custom hosts like dev.local.
 */
export function resolveApiBaseUrl(
  envUrl?: string | null,
  runtimeUrl?: string | null,
): string {
  for (const candidate of [runtimeUrl, envUrl]) {
    if (candidate === undefined || candidate === null) continue;
    const trimmed = candidate.trim();
    if (trimmed === "" || trimmed === "/" || trimmed === "same-origin") {
      return "";
    }
    return trimmed.replace(/\/$/, "");
  }
  return "http://127.0.0.1:4000";
}

/**
 * Resolve the public Streamable HTTP MCP resource URL.
 *
 * MCP OAuth binds tokens to this exact resource, so a deployment may not
 * silently derive a different hostname when the API and MCP listeners have
 * separate public names. Runtime config wins for edge deployments; an empty
 * or same-origin value intentionally follows the dashboard/API origin.
 */
export function resolveMCPBaseUrl(
  envUrl?: string | null,
  runtimeUrl?: string | null,
  apiUrl = "",
): string {
  for (const candidate of [runtimeUrl, envUrl]) {
    if (candidate === undefined || candidate === null) continue;
    const trimmed = candidate.trim();
    if (trimmed === "" || trimmed === "/" || trimmed === "same-origin") {
      return apiUrl ? `${apiUrl.replace(/\/$/, "")}/mcp` : "/mcp";
    }
    const normalized = trimmed.replace(/\/+$/, "");
    return normalized.endsWith("/mcp") ? normalized : `${normalized}/mcp`;
  }
  return apiUrl ? `${apiUrl.replace(/\/$/, "")}/mcp` : "/mcp";
}

function readRuntimeUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.__TINYBASE_URL__;
}

function readEnvUrl(): string | undefined {
  try {
    return import.meta.env.VITE_TINYBASE_URL as string | undefined;
  } catch {
    return undefined;
  }
}

function readRuntimeMCPUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.__TINYBASE_MCP_URL__;
}

function readEnvMCPUrl(): string | undefined {
  try {
    return import.meta.env.VITE_TINYBASE_MCP_URL as string | undefined;
  } catch {
    return undefined;
  }
}

const API_URL = resolveApiBaseUrl(readEnvUrl(), readRuntimeUrl());

const ACCESS_TOKEN_KEY = "tb_platform_token";
const SESSION_KEY = "tb_platform_session";

export type ApiError = { code: string; message: string; details?: unknown };

export class ApiClientError extends Error {
  code: string;
  details?: unknown;
  constructor(err: ApiError) {
    super(err.message);
    this.code = err.code;
    this.details = err.details;
  }
}

type Envelope<T> = {
  data: T;
  error: ApiError | null;
  meta: { request_id: string; count?: number };
};

export type PlatformUser = {
  id: string;
  email: string;
  username?: string | null;
  status: string;
  operator_role?: "verification_operator" | "platform_admin";
  created_at: string;
};

export type Session = {
  /** Present when status is authenticated (or omitted for legacy usable sessions). */
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  refresh_expires_in?: number;
  /** Client-side absolute expiry (ms since epoch). */
  expires_at?: number;
  user: PlatformUser;
  /**
   * Auth v2: "pending_verification" means signup succeeded without a usable session.
   * "authenticated" means access+refresh tokens are present.
   */
  status?: "authenticated" | "pending_verification" | string;
};

export type StoredSession = {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  expires_at?: number;
  user?: PlatformUser;
};

/** Auth v2 OTP issue/resend public response (enumeration-safe). */
export type PlatformOTPIssueResult = {
  status: string;
  message: string;
  expires_in?: number;
};

/** True when the session can access the platform data/API plane. */
export function isUsablePlatformSession(
  session: Session | null | undefined,
): session is Session & { access_token: string; refresh_token: string } {
  return Boolean(
    session?.access_token &&
      session.refresh_token &&
      session.status !== "pending_verification",
  );
}

function normalizeSession(session: Session): Session {
  if (!session.access_token) {
    return session;
  }
  const expires_at =
    session.expires_at ?? Date.now() + (session.expires_in ?? 900) * 1000;
  return { ...session, expires_at, status: session.status ?? "authenticated" };
}

/** Definitive platform refresh failures that warrant clearing local session. */
export function isDefinitivePlatformAuthFailure(code: string | undefined): boolean {
  if (!code) return false;
  switch (code) {
    case "INVALID_GRANT":
    case "REFRESH_TOKEN_INVALID":
    case "REFRESH_TOKEN_REUSED":
    case "TOKEN_INVALID":
    case "INVALID_TOKEN":
    case "AUTH_REQUIRED":
    case "INVALID_CREDENTIALS":
    case "invalid_grant":
      return true;
    default:
      return false;
  }
}

export function getStoredSession(
  storage: Storage | null = typeof window !== "undefined" ? localStorage : null,
): StoredSession | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SESSION_KEY);
    if (raw) {
      return JSON.parse(raw) as StoredSession;
    }
    // Migrate legacy access-token-only storage.
    const legacy = storage.getItem(ACCESS_TOKEN_KEY);
    if (legacy) {
      return { access_token: legacy };
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function setStoredSession(
  session: Session | null,
  storage: Storage | null = typeof window !== "undefined" ? localStorage : null,
) {
  if (!storage) return;
  if (!session || !isUsablePlatformSession(session)) {
    storage.removeItem(SESSION_KEY);
    storage.removeItem(ACCESS_TOKEN_KEY);
    return;
  }
  const normalized = normalizeSession(session);
  storage.setItem(
    SESSION_KEY,
    JSON.stringify({
      access_token: normalized.access_token!,
      refresh_token: normalized.refresh_token,
      expires_in: normalized.expires_in,
      expires_at: normalized.expires_at,
      user: normalized.user,
    } satisfies StoredSession),
  );
  // Keep legacy key in sync for any external tooling.
  storage.setItem(ACCESS_TOKEN_KEY, normalized.access_token!);
}

function token(): string | null {
  return getStoredSession()?.access_token ?? null;
}

export function setToken(t: string | null) {
  if (t == null) {
    setStoredSession(null);
    return;
  }
  // Partial update is not supported; use setStoredSession for full sessions.
  if (typeof window === "undefined") return;
  localStorage.setItem(ACCESS_TOKEN_KEY, t);
  const existing = getStoredSession();
  if (existing) {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ ...existing, access_token: t }),
    );
  }
}

export function getToken() {
  return token();
}

export function apiBase() {
  return resolveApiBaseUrl(readEnvUrl(), readRuntimeUrl()) || API_URL;
}

/** Canonical MCP resource URL used by the dashboard connection guide. */
export function mcpBase() {
  return resolveMCPBaseUrl(readEnvMCPUrl(), readRuntimeMCPUrl(), apiBase());
}

let refreshInFlight: Promise<Session | null> | null = null;
let cookieRefreshInFlight: Promise<PlatformUser | null> | null = null;

/**
 * Refresh access token using stored refresh_token.
 * Returns the new session, or null when refresh could not produce tokens.
 * Clears storage only on definitive auth failure (D-0073); keeps session on
 * transient network / 5xx style failures so the caller can retry.
 */
export async function refreshPlatformSession(): Promise<Session | null> {
  if (typeof window === "undefined") return null;
  if (refreshInFlight) return refreshInFlight;

  const base = apiBase();
  refreshInFlight = (async () => {
    const stored = getStoredSession();
    if (!stored?.refresh_token) return null;
    try {
      const res = await trackedFetch(`${base}/v1/platform/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: stored.refresh_token }),
      });
      let json: Envelope<Session>;
      try {
        json = (await res.json()) as Envelope<Session>;
      } catch {
        // Non-JSON / transport glitch — keep stored session for retry.
        return null;
      }
      if (json.error || !json.data || !isUsablePlatformSession(json.data)) {
        if (isDefinitivePlatformAuthFailure(json.error?.code)) {
          setStoredSession(null);
        }
        return null;
      }
      const session = normalizeSession(json.data);
      setStoredSession(session);
      return session;
    } catch {
      // Transient network failure — do not clear session (D-0073 / US-121).
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function currentPlatformCookieUser(): Promise<PlatformUser | null> {
  try {
    const response = await trackedFetch(`${apiBase()}/v1/platform/auth/me`, {
      credentials: "include",
    });
    const envelope = (await response.json()) as Envelope<PlatformUser>;
    return response.ok && !envelope.error ? envelope.data : null;
  } catch {
    return null;
  }
}

async function rotatePlatformCookieSession(): Promise<PlatformUser | null> {
  // Another dashboard tab may have refreshed while this caller was waiting
  // for the cross-tab lock. Re-check the shared HttpOnly access cookie before
  // rotating the refresh token, otherwise a benign concurrent visit can look
  // like refresh-token replay and revoke the whole session family.
  const current = await currentPlatformCookieUser();
  if (current) return current;

  try {
    const response = await trackedFetch(`${apiBase()}/portal/platform/refresh`, {
      method: "POST",
      credentials: "include",
    });
    const envelope = (await response.json()) as Envelope<{
      user: PlatformUser;
    }>;
    return response.ok && !envelope.error ? envelope.data.user : null;
  } catch {
    return null;
  }
}

export async function refreshPlatformCookieSession(): Promise<PlatformUser | null> {
  if (cookieRefreshInFlight) return cookieRefreshInFlight;

  cookieRefreshInFlight = (async () => {
    if (typeof navigator !== "undefined" && navigator.locks) {
      return navigator.locks.request(
        "tinybase-platform-session-refresh",
        rotatePlatformCookieSession,
      );
    }
    return rotatePlatformCookieSession();
  })().finally(() => {
    cookieRefreshInFlight = null;
  });

  return cookieRefreshInFlight;
}

export function isAccessTokenExpiringSoon(skewMs = 60_000): boolean {
  const stored = getStoredSession();
  if (!stored?.expires_at) return false;
  return Date.now() >= stored.expires_at - skewMs;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  auth = true,
  retried = false,
): Promise<{ data: T; count?: number }> {
  // Count only the outermost attempt so retries do not double-count loading.
  const endLoading = retried ? () => {} : beginLoading();
  try {
    const headers = new Headers(init.headers);
    if (!headers.has("Content-Type") && init.body) {
      headers.set("Content-Type", "application/json");
    }
    if (auth) {
      if (isAccessTokenExpiringSoon()) {
        await refreshPlatformSession();
      }
      const t = token();
      if (t) headers.set("Authorization", `Bearer ${t}`);
    }
    const res = await fetch(`${apiBase()}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
    const json = (await res.json()) as Envelope<T>;
    if (json.error) {
      const code = json.error.code;
      const isAuthFail =
        auth &&
        !retried &&
        (res.status === 401 ||
          code === "AUTH_REQUIRED" ||
          code === "INVALID_TOKEN" ||
          code === "TOKEN_EXPIRED");
      if (isAuthFail) {
        const refreshed = getStoredSession()?.refresh_token
          ? await refreshPlatformSession()
          : await refreshPlatformCookieSession();
        if (refreshed) {
          // await so finally/endLoading runs only after the full attempt chain
          return await request<T>(path, init, auth, true);
        }
      }
      throw new ApiClientError(json.error);
    }
    return { data: json.data, count: json.meta?.count };
  } finally {
    endLoading();
  }
}

export type Project = {
  id: string;
  owner_id: string;
  name: string;
  slug: string;
  schema_name: string;
  status: string;
  allowed_origins: string[];
  created_at: string;
  updated_at: string;
};

export type ProjectDatabaseSnapshot = {
  placement: { mode: "legacy_schema" | "isolated_cluster"; engine: string; engine_version?: string; desired_state: string; observed_state: string; generation: number; resource_profile: Record<string, unknown>; last_accessed_at: string; last_disk_usage_mb?: number; updated_at: string };
  topology: { physical_nodes: number; control_database: string; project_database: string; high_availability: false; same_node_replica_is_ha: false };
  capabilities: { external_backup: boolean; pitr_restore: boolean; legacy_migration: boolean };
  operations: { operation_id: string; kind: string; state: string; step: string; attempts: number; error_code?: string; reason?: string; created_at: string }[];
  backups: { backup_id: string; kind: string; state: string; repository: string; external_target: boolean; size_bytes?: number; backup_time?: string; error_code?: string }[];
  restores: { restore_id: string; backup_id: string; source_generation: number; target_generation: number; target_time?: string; state: string; error_code?: string; created_at: string }[];
  legacy_migrations: { migration_id: string; state: string; source_generation: number; target_generation: number; validation_report: Record<string, unknown>; error_code?: string; rollback_deadline?: string; updated_at: string }[];
};

export type ProjectDatabaseAction = {
  kind: string;
  reason: string;
  /** Required on every platform owner call — password reauthentication, not a session time-window. */
  current_password: string;
  confirmation?: string;
  backup_id?: string;
  migration_id?: string;
  target_time?: string;
  idempotency_key?: string;
};

export type ProjectAuthPolicy = {
  project_id: string;
  email_verification: "optional" | "required";
  verification_token_ttl_seconds: number;
  password_reset_token_ttl_seconds: number;
  password_reset_revoke_all_sessions: true;
  updated_at: string;
};

export type CreateProjectResult = {
  project: Project;
  public_key: string;
  service_key: string;
};

export type ApplicationBranding = {
  primary_color: string;
  accent_color: string;
  background_color: string;
  text_color: string;
  display_mode: "card" | "centered";
};

export type ApplicationSecurity = {
  pkce_required: boolean;
  access_token_ttl_seconds: number;
  refresh_token_ttl_seconds: number;
  session_ttl_seconds: number;
};

export type ApplicationConfigInput = {
  slug: string;
  name: string;
  logo_url?: string;
  logo_dark_url?: string;
  description?: string;
  developer_name?: string;
  developer_url?: string;
  homepage_url?: string;
  privacy_policy_url?: string;
  terms_url?: string;
  support_contact?: string;
  redirect_uris: string[];
  declared_domains?: string[];
  allowed_scopes?: ("profile" | "email" | "offline_access")[];
  branding: ApplicationBranding;
  security: ApplicationSecurity;
};

export type Application = ApplicationConfigInput & {
  id: string;
  project_id: string;
  client_id: string;
  status: "active" | "disabled";
  redirect_uris: string[];
  declared_domains: string[];
  allowed_scopes: ("profile" | "email" | "offline_access")[];
  eVerify: boolean;
  review_status: "not_requested" | "pending" | "approved" | "denied";
  review_requested_at?: string | null;
  last_configuration_change_at: string;
  last_authorization_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type GoogleProviderConfig = {
  application_id: string;
  provider: "google";
  client_id: string;
  redirect_uri: string;
  enabled: boolean;
  configured: boolean;
  updated_at?: string;
};

export type GoogleProviderConfigInput = {
  client_id: string;
  client_secret?: string;
  redirect_uri: string;
  enabled: boolean;
};

export type VerificationOperator = {
  user_id: string;
  role: "verification_operator" | "platform_admin";
  created_at: string;
};

export type VerificationReview = {
  id: string;
  application_id: string;
  project_id: string;
  project_name: string;
  status: "pending" | "approved" | "denied" | "cancelled";
  snapshot: Application;
  requested_by: string;
  requested_at: string;
  resolved_by?: string | null;
  resolved_at?: string | null;
  resolution_reason?: string | null;
  application: Application;
};

export type VerificationAuditEvent = {
  id: string;
  application_id: string;
  project_id: string;
  actor_id?: string | null;
  actor_type: "project_owner" | "operator" | "system";
  event_type:
    | "review_requested"
    | "review_cancelled"
    | "approved"
    | "denied"
    | "revoked"
    | "automatic_reset";
  old_e_verify: boolean;
  new_e_verify: boolean;
  reason: string;
  snapshot: Application;
  created_at: string;
};

export type APIKey = {
  id: string;
  project_id: string;
  key_type: string;
  key_prefix: string;
  name?: string | null;
  last_used_at?: string | null;
  revoked_at?: string | null;
  created_at: string;
  revealable: boolean;
};

export type RevealedAPIKey = {
  id: string;
  key_type: string;
  key: string;
};

export type Column = {
  name: string;
  data_type: string;
  udt_name: string;
  is_nullable: boolean;
  column_default?: string | null;
  is_primary_key: boolean;
};

export type SchemaTable = {
  name: string;
  columns: Column[];
  indexes?: {
    name: string;
    definition: string;
    unique: boolean;
    primary: boolean;
  }[];
  constraints?: {
    name: string;
    type: string;
    definition: string;
  }[];
  policies?: {
    name: string;
    command: string;
    roles: string[];
    using?: string | null;
    check?: string | null;
    permissive: boolean;
  }[];
  rls_enabled?: boolean;
  rls_forced?: boolean;
  description?: string | null;
  row_count?: number;
  size_bytes?: number;
};

export type SchemaView = {
  name: string;
  columns: Column[];
  definition?: string;
};

export type SchemaFunction = {
  name: string;
  kind: string;
  arguments: string;
  returns: string;
  language: string;
  security_definer: boolean;
};

export type SchemaTrigger = {
  name: string;
  table: string;
  definition: string;
  enabled: string;
};

export type Schema = {
  schema_name: string;
  tables: SchemaTable[];
  views: SchemaView[];
  functions?: SchemaFunction[];
  triggers?: SchemaTrigger[];
  roles?: { name: string; can_login: boolean; superuser: boolean }[];
  extensions?: { name: string; version: string }[];
  publications?: { name: string; tables: string[] }[];
  settings?: { name: string; setting: string; source: string }[];
};

/** Seed a read-only SELECT for a table or view name (double-quoted identifier). */
export function seedSelectSQL(relation: string, limit = 100): string {
  const safe = relation.replace(/"/g, '""');
  return `SELECT * FROM "${safe}" LIMIT ${limit};`;
}

export type Migration = {
  id: string;
  project_id: string;
  version: number;
  name?: string | null;
  sql_text: string;
  checksum: string;
  source: string;
  status: string;
  execution_time_ms?: number | null;
  error_message?: string | null;
  created_at: string;
};

export type Permission = {
  project_id: string;
  schema_name: string;
  table_name: string;
  select: string;
  insert: string;
  update: string;
  delete: string;
  owner_column?: string | null;
  /** Custom USING expression when select === "custom" */
  select_using?: string | null;
  /** Custom WITH CHECK when insert === "custom" */
  insert_check?: string | null;
  update_using?: string | null;
  update_check?: string | null;
  delete_using?: string | null;
};

export type LivePolicy = {
  name: string;
  command: string;
  roles: string[];
  using?: string | null;
  check?: string | null;
  permissive: boolean;
  managed: boolean;
};

export type TableRLS = {
  schema_name: string;
  table_name: string;
  enabled: boolean;
  forced: boolean;
  policies: LivePolicy[];
};

export type SecurityFinding = {
  class: string;
  severity: string;
  object: string;
  title: string;
  detail: string;
};

export type SecurityAdvisorReport = {
  scanned_at: string;
  findings: SecurityFinding[];
};

/** Advanced per-table rate limit for Data REST `/v1/data/:table` (US-081). */
export type DataEndpointRateLimit = {
  project_id: string;
  table_name: string;
  /** Stored override; null means use gateway default for that key type. */
  public_rpm: number | null;
  service_rpm: number | null;
  public_overridden: boolean;
  service_overridden: boolean;
  using_defaults: boolean;
  /** Resolved limit actually enforced (override or gateway default). */
  effective_public_rpm: number;
  effective_service_rpm: number;
  default_public_rpm: number;
  default_service_rpm: number;
  created_at?: string;
  updated_at?: string;
};

/**
 * Partial update body.
 * - Omit a field: keep existing override for that key type.
 * - Number: set override.
 * - null: clear override (gateway default).
 */
export type DataEndpointRateLimitInput = {
  public_rpm?: number | null;
  service_rpm?: number | null;
};

export type AppUser = {
  id: string;
  email?: string | null;
  username?: string | null;
  status: string;
  email_verified: boolean;
  last_sign_in_at?: string | null;
  created_at: string;
};

/** Redacted refresh-token session (no token_hash). */
export type AuthSession = {
  id: string;
  user_id: string;
  email?: string | null;
  username?: string | null;
  expires_at: string;
  revoked_at?: string | null;
  user_agent?: string | null;
  ip_hash?: string | null;
  created_at: string;
  active: boolean;
};

export type AuthCatalogColumn = {
  name: string;
  type: string;
  nullable: boolean;
  sensitive: boolean;
  description: string;
  exposed: boolean;
};

export type AuthCatalogRelation = {
  name: string;
  description: string;
  source: string;
  columns: AuthCatalogColumn[];
};

/** MCP agent OAuth grant (connected agent). */
export type AgentGrant = {
  id: string;
  client_id: string;
  client_name: string;
  project_id?: string;
  project_name?: string;
  scopes: string[];
  resource: string;
  first_authorized_at: string;
  last_authorized_at: string;
  last_used_at?: string | null;
  revoked_at?: string | null;
};

/** Safe auth catalog: auth.users is SQL-readable; sessions remain owner-API only. */
export type AuthCatalog = {
  schema: string;
  description: string;
  relations: AuthCatalogRelation[];
};

export type ListAppUsersParams = {
  status?: string;
  q?: string;
  sort?: string;
};

export type ListAuthSessionsParams = {
  active?: boolean | "all";
  user_id?: string;
};

export type UsageSummary = {
  project_id: string;
  from: string;
  to: string;
  api_requests: number;
  auth_requests: number;
  database_queries: number;
  rows_read: number;
  rows_written: number;
  error_count: number;
  buckets: {
    bucket_start: string;
    api_requests: number;
    database_queries: number;
    rows_read: number;
    rows_written: number;
    error_count: number;
  }[];
};

export const api = {
  /**
   * Platform owner sign-up (Auth v2 first-party).
   * Email sign-up returns pending_verification without a usable session.
   * Turnstile token is accepted for back-compat but not required (D-0072).
   */
  signup: (
    email: string,
    password: string,
    username?: string,
    turnstileToken?: string,
  ) =>
    request<Session>(
      "/v1/platform/auth/signup",
      {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          username,
          turnstile_token: turnstileToken || undefined,
        }),
      },
      false,
    ),

  /**
   * Platform owner sign-in (Auth v2 first-party).
   * Unverified accounts throw ApiClientError with code EMAIL_NOT_VERIFIED.
   */
  signin: (email: string, password: string, turnstileToken?: string) =>
    request<Session>(
      "/v1/platform/auth/signin",
      {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          turnstile_token: turnstileToken || undefined,
        }),
      },
      false,
    ),

  /** Issue a 6-digit platform email OTP (Auth v2). */
  issueEmailOtp: (input: {
    email?: string;
    userId?: string;
    purpose?: string;
  }) =>
    request<PlatformOTPIssueResult>(
      "/v1/platform/auth/otp/issue",
      {
        method: "POST",
        body: JSON.stringify({
          purpose: input.purpose ?? "verify_email",
          email: input.email ?? "",
          user_id: input.userId ?? "",
        }),
      },
      false,
    ),

  /** Resend platform email OTP (supersedes prior code). */
  resendEmailOtp: (input: {
    email?: string;
    userId?: string;
    purpose?: string;
  }) =>
    request<PlatformOTPIssueResult>(
      "/v1/platform/auth/otp/resend",
      {
        method: "POST",
        body: JSON.stringify({
          purpose: input.purpose ?? "verify_email",
          email: input.email ?? "",
          user_id: input.userId ?? "",
        }),
      },
      false,
    ),

  /**
   * Verify platform email OTP. On success for verify_email, returns a usable session.
   */
  verifyEmailOtp: (input: {
    code: string;
    email?: string;
    userId?: string;
    purpose?: string;
  }) =>
    request<Session>(
      "/v1/platform/auth/otp/verify",
      {
        method: "POST",
        body: JSON.stringify({
          purpose: input.purpose ?? "verify_email",
          code: input.code,
          email: input.email ?? "",
          user_id: input.userId ?? "",
        }),
      },
      false,
    ),

  /** Enumeration-safe forgot password (sends OTP when eligible). */
  forgotPassword: (email: string) =>
    request<PlatformOTPIssueResult>(
      "/v1/platform/auth/forgot-password",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
      false,
    ),

  /**
   * Reset platform password with 6-digit OTP. Does not auto sign-in.
   */
  resetPassword: (input: {
    email: string;
    code: string;
    newPassword: string;
  }) =>
    request<{ status: string }>(
      "/v1/platform/auth/reset-password",
      {
        method: "POST",
        body: JSON.stringify({
          email: input.email,
          code: input.code,
          new_password: input.newPassword,
        }),
      },
      false,
    ),

  signout: (refreshToken: string) =>
    request<{ status: string }>(
      "/v1/platform/auth/signout",
      {
        method: "POST",
        body: JSON.stringify({ refresh_token: refreshToken }),
      },
      false,
    ),

  portalSignout: () =>
    request<{ status: string }>(
      "/portal/platform/signout",
      { method: "POST" },
      false,
    ),

  me: () => request<PlatformUser>("/v1/platform/auth/me"),

  listProjects: () => request<Project[]>("/v1/platform/projects"),

  createProject: (name: string) =>
    request<CreateProjectResult>("/v1/platform/projects", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  getProject: (id: string) => request<Project>(`/v1/platform/projects/${id}`),

  getProjectDatabase: (id: string) => request<ProjectDatabaseSnapshot>(`/v1/platform/projects/${id}/database`),

  projectDatabaseAction: (id: string, body: ProjectDatabaseAction) => request<{operation_id:string;state:string}>(`/v1/platform/projects/${id}/database/actions`, { method: "POST", body: JSON.stringify(body) }),

  manageProjectDatabase: (id: string, body: Record<string, unknown>) => request<{operation_id:string;state:string}>(`/v1/platform/projects/${id}/database/operations`, { method: "POST", body: JSON.stringify(body) }),

  updateProjectCORS: (id: string, origins: string[]) =>
    request<Project>(`/v1/platform/projects/${id}/cors`, {
      method: "PUT",
      body: JSON.stringify({ origins }),
    }),

  getProjectAuthPolicy: (id: string) =>
    request<ProjectAuthPolicy>(`/v1/platform/projects/${id}/auth-policy`),

  updateProjectAuthPolicy: (id: string, policy: ProjectAuthPolicy) =>
    request<ProjectAuthPolicy>(`/v1/platform/projects/${id}/auth-policy`, {
      method: "PUT",
      body: JSON.stringify(policy),
    }),

  bootstrapProject: (id: string, template: "notes") =>
    request<{ template: string; created: boolean; permissions: Permission }>(
      `/v1/platform/projects/${id}/bootstrap`,
      {
        method: "POST",
        body: JSON.stringify({ template }),
      },
    ),

  deleteProject: (id: string) =>
    request<{ status: string }>(`/v1/platform/projects/${id}`, {
      method: "DELETE",
    }),

  listAgentGrants: () =>
    request<AgentGrant[]>("/v1/platform/agent-grants"),

  revokeAgentGrant: (grantId: string) =>
    request<{ status?: string }>(`/v1/platform/agent-grants/${grantId}/revoke`, {
      method: "POST",
    }),

  listProjectAgentGrants: (projectId: string) =>
    request<AgentGrant[]>(
      `/v1/platform/projects/${projectId}/agent-grants`,
    ),

  revokeProjectAgentGrant: (projectId: string, grantId: string) =>
    request<{ status?: string }>(
      `/v1/platform/projects/${projectId}/agent-grants/${grantId}/revoke`,
      { method: "POST" },
    ),

  listApplications: (projectId: string) =>
    request<Application[]>(`/v1/platform/projects/${projectId}/applications`),

  createApplication: (projectId: string, body: ApplicationConfigInput) =>
    request<Application>(`/v1/platform/projects/${projectId}/applications`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getApplication: (projectId: string, applicationId: string) =>
    request<Application>(
      `/v1/platform/projects/${projectId}/applications/${applicationId}`,
    ),

  updateApplication: (
    projectId: string,
    applicationId: string,
    body: ApplicationConfigInput,
  ) =>
    request<Application>(
      `/v1/platform/projects/${projectId}/applications/${applicationId}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  setApplicationEnabled: (
    projectId: string,
    applicationId: string,
    enabled: boolean,
  ) =>
    request<Application>(
      `/v1/platform/projects/${projectId}/applications/${applicationId}/${enabled ? "enable" : "disable"}`,
      { method: "POST", body: "{}" },
    ),

  deleteApplication: (projectId: string, applicationId: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${projectId}/applications/${applicationId}`,
      { method: "DELETE" },
    ),

  requestApplicationVerification: (projectId: string, applicationId: string) =>
    request<Application>(
      `/v1/platform/projects/${projectId}/applications/${applicationId}/verification/request`,
      { method: "POST", body: "{}" },
    ),

  cancelApplicationVerification: (projectId: string, applicationId: string) =>
    request<Application>(
      `/v1/platform/projects/${projectId}/applications/${applicationId}/verification/cancel`,
      { method: "POST", body: "{}" },
    ),

  verificationOperatorMe: () =>
    request<VerificationOperator>("/v1/platform/operator/me"),

  listApplicationVerifications: (status = "pending") =>
    request<VerificationReview[]>(
      `/v1/platform/operator/application-verifications?status=${encodeURIComponent(status)}`,
    ),

  getApplicationVerification: (applicationId: string) =>
    request<{ review: VerificationReview; audit: VerificationAuditEvent[] }>(
      `/v1/platform/operator/application-verifications/${applicationId}`,
    ),

  resolveApplicationVerification: (
    applicationId: string,
    action: "approve" | "deny" | "revoke",
    reason: string,
    currentPassword: string,
  ) =>
    request<Application>(
      `/v1/platform/operator/application-verifications/${applicationId}/${action}`,
      {
        method: "POST",
        body: JSON.stringify({ reason, current_password: currentPassword }),
      },
    ),

  listKeys: (id: string) =>
    request<APIKey[]>(`/v1/platform/projects/${id}/keys`),

  revealKey: (projectId: string, keyId: string) =>
    request<RevealedAPIKey>(
      `/v1/platform/projects/${projectId}/keys/${keyId}/reveal`,
    ),

  issueKey: (projectId: string, keyType: "public" | "service") =>
    request<RevealedAPIKey>(`/v1/platform/projects/${projectId}/keys`, {
      method: "POST",
      body: JSON.stringify({ key_type: keyType }),
    }),

  getSchema: (id: string) =>
    request<Schema>(`/v1/platform/projects/${id}/schema`),

  describeTable: (id: string, table: string) =>
    request<SchemaTable>(`/v1/platform/projects/${id}/schema/tables/${table}`),

  describeView: (id: string, view: string) =>
    request<SchemaView>(`/v1/platform/projects/${id}/schema/views/${view}`),

  listMigrations: (id: string) =>
    request<Migration[]>(`/v1/platform/projects/${id}/migrations`),

  sqlQuery: (id: string, sql: string) =>
    request<{
      columns: string[];
      rows: Record<string, unknown>[];
      row_count: number;
    }>(`/v1/platform/projects/${id}/sql/query`, {
      method: "POST",
      body: JSON.stringify({ sql }),
    }),

  getGoogleProviderConfig: (projectId: string, applicationId: string) =>
    request<GoogleProviderConfig>(
      `/v1/platform/projects/${projectId}/applications/${applicationId}/providers/google`,
    ),

  updateGoogleProviderConfig: (
    projectId: string,
    applicationId: string,
    input: GoogleProviderConfigInput,
  ) =>
    request<GoogleProviderConfig>(
      `/v1/platform/projects/${projectId}/applications/${applicationId}/providers/google`,
      { method: "PUT", body: JSON.stringify(input) },
    ),

  sqlPlan: (id: string, sql: string) =>
    request<{
      statements: {
        index: number;
        type: string;
        object?: string;
        destructive: boolean;
      }[];
      warnings: string[];
      checksum: string;
    }>(`/v1/platform/projects/${id}/sql/plan`, {
      method: "POST",
      body: JSON.stringify({ sql }),
    }),

  sqlMigrate: (id: string, sql: string, name?: string) =>
    request<{ migration: Migration; warnings: string[] }>(
      `/v1/platform/projects/${id}/sql/migrate`,
      {
        method: "POST",
        body: JSON.stringify({ sql, name, source: "dashboard" }),
      },
    ),

  listPermissions: (id: string) =>
    request<Permission[]>(`/v1/platform/projects/${id}/permissions`),

  setPermission: (id: string, table: string, body: Record<string, unknown>) =>
    request<Permission>(`/v1/platform/projects/${id}/permissions/${table}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  getTableRLS: (id: string, table: string) =>
    request<TableRLS>(
      `/v1/platform/projects/${id}/permissions/${encodeURIComponent(table)}/rls`,
    ),

  setTableRLS: (
    id: string,
    table: string,
    body: { enabled: boolean; force?: boolean },
  ) =>
    request<TableRLS>(
      `/v1/platform/projects/${id}/permissions/${encodeURIComponent(table)}/rls`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  createPolicy: (id: string, table: string, body: Record<string, unknown>) =>
    request<TableRLS>(
      `/v1/platform/projects/${id}/permissions/${encodeURIComponent(table)}/policies`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  alterPolicy: (
    id: string,
    table: string,
    name: string,
    body: Record<string, unknown>,
  ) =>
    request<TableRLS>(
      `/v1/platform/projects/${id}/permissions/${encodeURIComponent(table)}/policies/${encodeURIComponent(name)}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  dropPolicy: (id: string, table: string, name: string) =>
    request<TableRLS>(
      `/v1/platform/projects/${id}/permissions/${encodeURIComponent(table)}/policies/${encodeURIComponent(name)}`,
      { method: "DELETE" },
    ),

  getSecurityAdvisor: (id: string) =>
    request<SecurityAdvisorReport>(
      `/v1/platform/projects/${id}/advisors/security`,
    ),

  runSecurityAdvisor: (id: string) =>
    request<SecurityAdvisorReport>(
      `/v1/platform/projects/${id}/advisors/security/run`,
      { method: "POST", body: "{}" },
    ),

  listDataEndpointRateLimits: (id: string) =>
    request<DataEndpointRateLimit[]>(
      `/v1/platform/projects/${id}/endpoint-settings/rate-limits`,
    ),

  getDataEndpointRateLimit: (id: string, table: string) =>
    request<DataEndpointRateLimit>(
      `/v1/platform/projects/${id}/endpoint-settings/rate-limits/${encodeURIComponent(table)}`,
    ),

  setDataEndpointRateLimit: (
    id: string,
    table: string,
    body: DataEndpointRateLimitInput,
  ) =>
    request<DataEndpointRateLimit>(
      `/v1/platform/projects/${id}/endpoint-settings/rate-limits/${encodeURIComponent(table)}`,
      {
        method: "PUT",
        body: JSON.stringify(body),
      },
    ),

  deleteDataEndpointRateLimit: (id: string, table: string) =>
    request<{ deleted: boolean; table_name: string }>(
      `/v1/platform/projects/${id}/endpoint-settings/rate-limits/${encodeURIComponent(table)}`,
      { method: "DELETE" },
    ),

  listPresets: () =>
    request<
      {
        name: string;
        select: string;
        insert: string;
        update: string;
        delete: string;
      }[]
    >("/v1/platform/permissions/presets"),

  listAppUsers: (id: string, params?: ListAppUsersParams) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.q) qs.set("q", params.q);
    if (params?.sort) qs.set("sort", params.sort);
    const q = qs.toString();
    return request<AppUser[]>(
      `/v1/platform/projects/${id}/users${q ? `?${q}` : ""}`,
    );
  },

  setAppUserStatus: (id: string, userId: string, status: string) =>
    request<AppUser>(`/v1/platform/projects/${id}/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  verifyAppUserEmail: (id: string, userId: string) =>
    request<AppUser>(
      `/v1/platform/projects/${id}/users/${userId}/verify-email`,
      { method: "POST", body: "{}" },
    ),

  deleteAppUser: (id: string, userId: string) =>
    request<{ status: string }>(`/v1/platform/projects/${id}/users/${userId}`, {
      method: "DELETE",
    }),

  revokeAppUserSessions: (id: string, userId: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/users/${userId}/revoke-sessions`,
      { method: "POST", body: "{}" },
    ),

  /** Time-boxed app JWT for owner debugging (US-069). Never log access_token. */
  impersonateAppUser: (id: string, userId: string) =>
    request<ImpersonationResult>(
      `/v1/platform/projects/${id}/users/${userId}/impersonate`,
      { method: "POST", body: "{}" },
    ),

  // --- Object storage (US-065 / US-072) ---
  listStorageBuckets: (id: string) =>
    request<StorageBucket[]>(
      `/v1/platform/projects/${id}/storage/buckets`,
    ),

  createStorageBucket: (
    id: string,
    body: { name: string; is_public?: boolean },
  ) =>
    request<StorageBucket>(`/v1/platform/projects/${id}/storage/buckets`, {
      method: "POST",
      body: JSON.stringify({
        name: body.name,
        is_public: Boolean(body.is_public),
      }),
    }),

  listStorageObjects: (id: string, bucket: string, prefix?: string) => {
    const qs =
      prefix != null && prefix !== ""
        ? `?prefix=${encodeURIComponent(prefix)}`
        : "";
    return request<StorageObjectMeta[]>(
      `/v1/platform/projects/${id}/storage/buckets/${encodeURIComponent(bucket)}/objects${qs}`,
    );
  },

  uploadStorageObject: async (
    id: string,
    bucket: string,
    objectPath: string,
    body: Blob | ArrayBuffer | string,
    contentType?: string,
  ) => {
    const headers = new Headers();
    if (contentType) headers.set("Content-Type", contentType);
    else if (typeof body === "string") {
      headers.set("Content-Type", "text/plain; charset=utf-8");
    } else {
      headers.set("Content-Type", "application/octet-stream");
    }
    if (isAccessTokenExpiringSoon()) {
      await refreshPlatformSession();
    }
    const t = token();
    if (t) headers.set("Authorization", `Bearer ${t}`);
    const path = `/v1/platform/projects/${id}/storage/buckets/${encodeURIComponent(bucket)}/objects?path=${encodeURIComponent(objectPath)}`;
    const res = await trackedFetch(`${apiBase()}${path}`, {
      method: "POST",
      headers,
      body,
      credentials: "include",
    });
    const json = (await res.json()) as Envelope<StorageObjectMeta>;
    if (json.error) throw new ApiClientError(json.error);
    return { data: json.data, count: json.meta?.count };
  },

  getStorageObjectMeta: (id: string, bucket: string, objectPath: string) =>
    request<StorageObjectMeta>(
      `/v1/platform/projects/${id}/storage/buckets/${encodeURIComponent(bucket)}/object?path=${encodeURIComponent(objectPath)}`,
    ),

  downloadStorageObject: async (
    id: string,
    bucket: string,
    objectPath: string,
    filename?: string,
  ) => {
    if (isAccessTokenExpiringSoon()) {
      await refreshPlatformSession();
    }
    const t = token();
    const path = `/v1/platform/projects/${id}/storage/buckets/${encodeURIComponent(bucket)}/object?path=${encodeURIComponent(objectPath)}&download=1`;
    const res = await trackedFetch(`${apiBase()}${path}`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      credentials: "include",
    });
    if (!res.ok) {
      throw new ApiClientError({
        code: "DOWNLOAD_FAILED",
        message: `download failed (${res.status})`,
      });
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || objectPath.split("/").pop() || "download";
    a.click();
    URL.revokeObjectURL(url);
  },

  deleteStorageObject: (id: string, bucket: string, objectPath: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/storage/buckets/${encodeURIComponent(bucket)}/object?path=${encodeURIComponent(objectPath)}`,
      { method: "DELETE" },
    ),

  signStorageObject: (
    id: string,
    bucket: string,
    body: { path: string; ttl_seconds?: number },
  ) =>
    request<StorageSignedURL>(
      `/v1/platform/projects/${id}/storage/buckets/${encodeURIComponent(bucket)}/sign`,
      {
        method: "POST",
        body: JSON.stringify({
          path: body.path,
          ttl_seconds: body.ttl_seconds ?? 3600,
        }),
      },
    ),

  // --- Images management (IN-042 / US-127+) ---
  listImages: (
    id: string,
    query?: { status?: string; visibility?: string; prefix?: string },
  ) => {
    const qs = new URLSearchParams();
    if (query?.status) qs.set("status", query.status);
    if (query?.visibility) qs.set("visibility", query.visibility);
    if (query?.prefix) qs.set("prefix", query.prefix);
    const s = qs.toString();
    return request<ProjectImage[]>(
      `/v1/platform/projects/${id}/images${s ? `?${s}` : ""}`,
    );
  },

  getImage: (id: string, imageId: string) =>
    request<ProjectImage>(
      `/v1/platform/projects/${id}/images/${encodeURIComponent(imageId)}`,
    ),

  uploadImage: async (
    id: string,
    body: Blob | ArrayBuffer | string,
    opts?: {
      filename?: string;
      alt?: string;
      visibility?: ImageVisibility;
      contentType?: string;
    },
  ) => {
    const headers = new Headers();
    const ct = opts?.contentType;
    if (ct) headers.set("Content-Type", ct);
    else if (typeof body === "string") {
      headers.set("Content-Type", "image/png");
    } else if (typeof Blob !== "undefined" && body instanceof Blob && body.type) {
      headers.set("Content-Type", body.type);
    } else {
      headers.set("Content-Type", "application/octet-stream");
    }
    if (isAccessTokenExpiringSoon()) {
      await refreshPlatformSession();
    }
    const t = token();
    if (t) headers.set("Authorization", `Bearer ${t}`);
    const qs = new URLSearchParams();
    if (opts?.filename) qs.set("filename", opts.filename);
    if (opts?.alt) qs.set("alt", opts.alt);
    if (opts?.visibility) qs.set("visibility", opts.visibility);
    const path = `/v1/platform/projects/${id}/images?${qs.toString()}`;
    const res = await trackedFetch(`${apiBase()}${path}`, {
      method: "POST",
      headers,
      body,
      credentials: "include",
    });
    const json = (await res.json()) as Envelope<{
      image: ProjectImage;
      job: ImageJob;
    }>;
    if (json.error) throw new ApiClientError(json.error);
    return { data: json.data, count: json.meta?.count };
  },

  patchImage: (
    id: string,
    imageId: string,
    body: {
      alt_text?: string;
      filename?: string;
      visibility?: ImageVisibility;
      focal_x?: number;
      focal_y?: number;
    },
  ) =>
    request<ProjectImage>(
      `/v1/platform/projects/${id}/images/${encodeURIComponent(imageId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  deleteImage: (id: string, imageId: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/images/${encodeURIComponent(imageId)}`,
      { method: "DELETE" },
    ),

  transformImage: (
    id: string,
    imageId: string,
    body: ImageTransformSpec,
  ) =>
    request<{ image: ProjectImage; job: ImageJob }>(
      `/v1/platform/projects/${id}/images/${encodeURIComponent(imageId)}/transform`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  reprocessImage: (id: string, imageId: string) =>
    request<{ image: ProjectImage; job: ImageJob }>(
      `/v1/platform/projects/${id}/images/${encodeURIComponent(imageId)}/reprocess`,
      { method: "POST", body: "{}" },
    ),

  signImage: (
    id: string,
    imageId: string,
    body: { tier?: string; format?: string; ttl_seconds?: number },
  ) =>
    request<ImageSignedURL>(
      `/v1/platform/projects/${id}/images/${encodeURIComponent(imageId)}/sign`,
      {
        method: "POST",
        body: JSON.stringify({
          tier: body.tier ?? "md",
          format: body.format ?? "webp",
          ttl_seconds: body.ttl_seconds ?? 3600,
        }),
      },
    ),

  // --- Edge Functions (US-077..079) ---
  listEdgeFunctions: (id: string) =>
    request<EdgeFunction[]>(`/v1/platform/projects/${id}/functions`),

  getEdgeFunction: (id: string, functionId: string) =>
    request<EdgeFunction>(
      `/v1/platform/projects/${id}/functions/${functionId}`,
    ),

  createEdgeFunction: (
    id: string,
    body: { name: string; slug: string; description?: string; source: string },
  ) =>
    request<EdgeFunction>(`/v1/platform/projects/${id}/functions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateEdgeFunction: (
    id: string,
    functionId: string,
    body: { name: string; description?: string },
  ) =>
    request<EdgeFunction>(
      `/v1/platform/projects/${id}/functions/${functionId}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  deployEdgeFunction: (id: string, functionId: string, source: string) =>
    request<EdgeFunction>(
      `/v1/platform/projects/${id}/functions/${functionId}/deploy`,
      { method: "POST", body: JSON.stringify({ source }) },
    ),

  setEdgeFunctionEnabled: (
    id: string,
    functionId: string,
    enabled: boolean,
  ) =>
    request<EdgeFunction>(
      `/v1/platform/projects/${id}/functions/${functionId}/status`,
      { method: "PUT", body: JSON.stringify({ enabled }) },
    ),

  deleteEdgeFunction: (id: string, functionId: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/functions/${functionId}`,
      { method: "DELETE" },
    ),

  listEdgeFunctionSecrets: (id: string) =>
    request<EdgeFunctionSecret[]>(
      `/v1/platform/projects/${id}/functions/secrets`,
    ),

  putEdgeFunctionSecret: (id: string, name: string, value: string) =>
    request<EdgeFunctionSecret>(
      `/v1/platform/projects/${id}/functions/secrets/${encodeURIComponent(name)}`,
      { method: "PUT", body: JSON.stringify({ value }) },
    ),

  deleteEdgeFunctionSecret: (id: string, name: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/functions/secrets/${encodeURIComponent(name)}`,
      { method: "DELETE" },
    ),

  edgeFunctionStats: (id: string) =>
    request<EdgeFunctionStats>(
      `/v1/platform/projects/${id}/functions/stats`,
    ),

  listEdgeFunctionInvocations: (id: string, functionId?: string) => {
    const query = functionId
      ? `?function_id=${encodeURIComponent(functionId)}`
      : "";
    return request<EdgeFunctionInvocation[]>(
      `/v1/platform/projects/${id}/functions/invocations${query}`,
    );
  },

  invokeEdgeFunction: async (
    id: string,
    functionId: string,
    body: string,
    method = "POST",
  ): Promise<EdgeFunctionTestResult> => {
    if (isAccessTokenExpiringSoon()) await refreshPlatformSession();
    const headers = new Headers({ "Content-Type": "application/json" });
    const accessToken = token();
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    const response = await trackedFetch(
      `${apiBase()}/v1/platform/projects/${id}/functions/${functionId}/invoke`,
      {
        method,
        headers,
        body: method === "GET" ? undefined : body,
        credentials: "include",
      },
    );
    const text = await response.text();
    if (!response.ok) {
      try {
        const envelope = JSON.parse(text) as Envelope<never>;
        if (envelope.error) throw new ApiClientError(envelope.error);
      } catch (error) {
        if (error instanceof ApiClientError) throw error;
      }
      throw new ApiClientError({
        code: "EDGE_INVOCATION_FAILED",
        message: text || `Invocation failed (${response.status})`,
      });
    }
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });
    return {
      status: response.status,
      body: text,
      headers: responseHeaders,
      invocation_id: response.headers.get("X-Edge-Invocation-Id"),
      version: response.headers.get("X-Edge-Function-Version"),
    };
  },

  listAuthSessions: (id: string, params?: ListAuthSessionsParams) => {
    const qs = new URLSearchParams();
    if (params?.active === "all") qs.set("active", "all");
    else if (params?.active === false) qs.set("active", "false");
    else if (params?.active === true) qs.set("active", "true");
    if (params?.user_id) qs.set("user_id", params.user_id);
    const q = qs.toString();
    return request<AuthSession[]>(
      `/v1/platform/projects/${id}/auth/sessions${q ? `?${q}` : ""}`,
    );
  },

  revokeAuthSession: (id: string, sessionId: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/auth/sessions/${sessionId}/revoke`,
      { method: "POST", body: "{}" },
    ),

  authCatalog: (id: string) =>
    request<AuthCatalog>(`/v1/platform/projects/${id}/auth/catalog`),

  usage: (id: string) =>
    request<UsageSummary>(`/v1/platform/projects/${id}/usage`),

  dataSelect: (id: string, table: string, qs = "") =>
    request<Record<string, unknown>[]>(
      `/v1/platform/projects/${id}/data/${table}${qs ? `?${qs}` : ""}`,
    ),

  dataInsert: (id: string, table: string, row: Record<string, unknown>) =>
    request<Record<string, unknown>[] | Record<string, unknown>>(
      `/v1/platform/projects/${id}/data/${table}`,
      { method: "POST", body: JSON.stringify(row) },
    ),

  dataDelete: (
    id: string,
    table: string,
    filterCol: string,
    filterVal: string,
  ) =>
    request<Record<string, unknown>[]>(
      `/v1/platform/projects/${id}/data/${table}?${encodeURIComponent(filterCol)}=eq.${encodeURIComponent(filterVal)}`,
      { method: "DELETE" },
    ),
  // --- Observability: API Gateway ---

  gatewayTraffic: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return request<TrafficSummary>(
      `/v1/platform/projects/${id}/observability/gateway/traffic${qs ? `?${qs}` : ""}`,
    );
  },
  gatewayErrors: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return request<ErrorSummary>(
      `/v1/platform/projects/${id}/observability/gateway/errors${qs ? `?${qs}` : ""}`,
    );
  },
  gatewayLatency: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return request<LatencySummary>(
      `/v1/platform/projects/${id}/observability/gateway/latency${qs ? `?${qs}` : ""}`,
    );
  },
  gatewayRoutes: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return request<RoutesSummary>(
      `/v1/platform/projects/${id}/observability/gateway/routes${qs ? `?${qs}` : ""}`,
    );
  },
  gatewayEgress: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return request<EgressSummary>(
      `/v1/platform/projects/${id}/observability/gateway/egress${qs ? `?${qs}` : ""}`,
    );
  },

  // --- Observability: Database ---
  databaseStorage: (id: string) =>
    request<DatabaseStorageReport>(
      `/v1/platform/projects/${id}/observability/database/storage`,
    ),
  databaseConnections: (id: string) =>
    request<DatabaseConnectionsReport>(
      `/v1/platform/projects/${id}/observability/database/connections`,
    ),
  databaseTables: (id: string) =>
    request<DatabaseTablesReport>(
      `/v1/platform/projects/${id}/observability/database/tables`,
    ),
  databaseLocks: (id: string) =>
    request<DatabaseLocksReport>(
      `/v1/platform/projects/${id}/observability/database/locks`,
    ),

  // --- Observability: Logs + Auth ---
  searchLogs: (
    id: string,
    opts?: {
      from?: string;
      to?: string;
      service?: string;
      severity?: string;
      request_id?: string;
      route?: string;
      method?: string;
      error_code?: string;
      status_family?: string;
      q?: string;
      cursor?: string;
      limit?: number;
    },
  ) => {
    const p = new URLSearchParams();
    if (opts?.from) p.set("from", opts.from);
    if (opts?.to) p.set("to", opts.to);
    if (opts?.service) p.set("service", opts.service);
    if (opts?.severity) p.set("severity", opts.severity);
    if (opts?.request_id) p.set("request_id", opts.request_id);
    if (opts?.route) p.set("route", opts.route);
    if (opts?.method) p.set("method", opts.method);
    if (opts?.error_code) p.set("error_code", opts.error_code);
    if (opts?.status_family) p.set("status_family", opts.status_family);
    if (opts?.q) p.set("q", opts.q);
    if (opts?.cursor) p.set("cursor", opts.cursor);
    if (opts?.limit) p.set("limit", String(opts.limit));
    const qs = p.toString();
    return request<LogSearchResult>(
      `/v1/platform/projects/${id}/observability/logs${qs ? `?${qs}` : ""}`,
    );
  },

  downloadLogsCSV: async (
    id: string,
    opts?: {
      service?: string;
      severity?: string;
      request_id?: string;
      q?: string;
    },
  ) => {
    const p = new URLSearchParams();
    if (opts?.service) p.set("service", opts.service);
    if (opts?.severity) p.set("severity", opts.severity);
    if (opts?.request_id) p.set("request_id", opts.request_id);
    if (opts?.q) p.set("q", opts.q);
    p.set("limit", "500");
    const qs = p.toString();
    const base = apiBase();
    const t = token();
    const res = await trackedFetch(
      `${base}/v1/platform/projects/${id}/observability/logs/export?${qs}`,
      {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      },
    );
    if (!res.ok) {
      throw new ApiClientError({
        code: "EXPORT_FAILED",
        message: `export failed (${res.status})`,
      });
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "logs.csv";
    a.click();
    URL.revokeObjectURL(url);
  },

  listLogViews: (id: string) =>
    request<SavedLogView[]>(
      `/v1/platform/projects/${id}/observability/logs/views`,
    ),

  saveLogView: (id: string, name: string, filters: Record<string, unknown>) =>
    request<SavedLogView>(
      `/v1/platform/projects/${id}/observability/logs/views`,
      {
        method: "POST",
        body: JSON.stringify({ name, filters }),
      },
    ),

  authReport: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return request<AuthReport>(
      `/v1/platform/projects/${id}/observability/auth${qs ? `?${qs}` : ""}`,
    );
  },

  /** MCP tool-call activity: stats + recent events (project owner). */
  mcpObservability: (
    id: string,
    opts?: { from?: string; to?: string; limit?: number },
  ) => {
    const p = new URLSearchParams();
    if (opts?.from) p.set("from", opts.from);
    if (opts?.to) p.set("to", opts.to);
    if (opts?.limit != null) p.set("limit", String(opts.limit));
    const qs = p.toString();
    return request<MCPObservabilityReport>(
      `/v1/platform/projects/${id}/observability/mcp${qs ? `?${qs}` : ""}`,
    );
  },

  mcpActivityEvents: (
    id: string,
    opts?: {
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
      tool?: string;
      result_class?: string;
    },
  ) => {
    const p = new URLSearchParams();
    if (opts?.from) p.set("from", opts.from);
    if (opts?.to) p.set("to", opts.to);
    if (opts?.limit != null) p.set("limit", String(opts.limit));
    if (opts?.offset != null) p.set("offset", String(opts.offset));
    if (opts?.tool) p.set("tool", opts.tool);
    if (opts?.result_class) p.set("result_class", opts.result_class);
    const qs = p.toString();
    return request<{ events: MCPActivityEvent[] }>(
      `/v1/platform/projects/${id}/observability/mcp/events${qs ? `?${qs}` : ""}`,
    );
  },

  mcpActivityStats: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return request<MCPActivityStats>(
      `/v1/platform/projects/${id}/observability/mcp/stats${qs ? `?${qs}` : ""}`,
    );
  },

  observabilityOverview: (id: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return request<OverviewResult>(
      `/v1/platform/projects/${id}/observability/overview${qs ? `?${qs}` : ""}`,
    );
  },

  usageQuotas: (id: string) =>
    request<QuotasReport>(`/v1/platform/projects/${id}/usage/quotas`),

  listAlertRules: (id: string) =>
    request<AlertRule[]>(
      `/v1/platform/projects/${id}/observability/alerts/rules`,
    ),
  createAlertRule: (
    id: string,
    body: {
      name: string;
      metric: string;
      operator: string;
      threshold: number;
      window_minutes?: number;
      severity?: string;
    },
  ) =>
    request<AlertRule>(
      `/v1/platform/projects/${id}/observability/alerts/rules`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  listAlertIncidents: (id: string, status?: string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<AlertIncident[]>(
      `/v1/platform/projects/${id}/observability/alerts/incidents${qs}`,
    );
  },
  ackIncident: (id: string, incidentId: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/observability/alerts/incidents/${incidentId}/acknowledge`,
      { method: "POST", body: "{}" },
    ),
  resolveIncident: (id: string, incidentId: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/observability/alerts/incidents/${incidentId}/resolve`,
      { method: "POST", body: "{}" },
    ),
  evaluateAlerts: (id: string) =>
    request<{ fired: number }>(
      `/v1/platform/projects/${id}/observability/alerts/evaluate`,
      { method: "POST", body: "{}" },
    ),
  metricCatalog: (id: string) =>
    request<MetricCatalogEntry[]>(
      `/v1/platform/projects/${id}/observability/reports/catalog`,
    ),
  listReports: (id: string) =>
    request<CustomReport[]>(
      `/v1/platform/projects/${id}/observability/reports`,
    ),
  saveReport: (id: string, name: string, layout: Record<string, unknown>) =>
    request<CustomReport>(`/v1/platform/projects/${id}/observability/reports`, {
      method: "POST",
      body: JSON.stringify({ name, layout }),
    }),
  deleteReport: (id: string, reportId: string) =>
    request<{ status: string }>(
      `/v1/platform/projects/${id}/observability/reports/${reportId}`,
      { method: "DELETE" },
    ),
};

// --- Observability types ---

export type TrafficSummary = {
  total_requests: number;
  requests_per_min: number;
  success_rate: number;
  by_status_family: Record<string, number>;
  by_service: Record<string, number>;
  by_method: Record<string, number>;
  by_route: {
    route_template: string;
    method: string;
    service: string;
    calls: number;
  }[];
  time_series: {
    bucket_start: string;
    total_requests: number;
    status_2xx: number;
    status_3xx: number;
    status_4xx: number;
    status_5xx: number;
  }[];
};

export type ErrorSummary = {
  total_requests: number;
  total_errors: number;
  error_rate: number;
  time_series: { bucket_start: string; count_4xx: number; count_5xx: number }[];
  top_error_routes: {
    route_template: string;
    method: string;
    error_count: number;
    error_rate: number;
  }[];
  recent_errors: {
    event_id: string;
    request_id: string;
    route_template: string;
    method: string;
    status_code: number;
    error_code: string;
    duration_ms: number;
    created_at: string;
  }[];
};

export type LatencySummary = {
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  time_series: {
    bucket_start: string;
    p50_ms: number;
    p95_ms: number;
    p99_ms: number;
  }[];
  slowest_routes: {
    route_template: string;
    method: string;
    calls: number;
    p50_ms: number;
    p95_ms: number;
    p99_ms: number;
    avg_duration_ms: number;
  }[];
};

export type RoutesSummary = {
  routes: {
    route_template: string;
    method: string;
    service: string;
    calls: number;
    error_rate: number;
    p50_ms: number;
    p95_ms: number;
    p99_ms: number;
    bytes_in: number;
    bytes_out: number;
  }[];
};

export type EgressSummary = {
  total_ingress_bytes: number;
  total_egress_bytes: number;
  avg_response_size: number;
  time_series: {
    bucket_start: string;
    ingress_bytes: number;
    egress_bytes: number;
  }[];
  top_egress_routes: {
    route_template: string;
    method: string;
    calls: number;
    total_egress_bytes: number;
    avg_response_bytes: number;
  }[];
};

export type DatabaseStorageReport = {
  schema_name: string;
  total_bytes: number;
  table_bytes: number;
  index_bytes: number;
  toast_bytes: number;
  relation_count: number;
  largest_tables: {
    name: string;
    total_bytes: number;
    table_bytes?: number;
    index_bytes?: number;
    estimated_rows?: number;
  }[];
  largest_indexes: { name: string; total_bytes: number }[];
  growth: {
    captured_at: string;
    total_bytes: number;
    table_bytes: number;
    index_bytes: number;
  }[];
  captured_at: string;
  attribution: string;
};

export type DatabaseConnectionsReport = {
  schema_name: string;
  active_sessions: number;
  idle_sessions: number;
  idle_in_transaction: number;
  total_project_sessions: number;
  host_total_sessions: number;
  max_connections: number;
  utilization_pct: number;
  by_state: Record<string, number>;
  by_application: Record<string, number>;
  sessions: {
    pid: number;
    state: string;
    application_name: string;
    user_name: string;
    query?: string;
    wait_event?: string;
  }[];
  attribution: string;
  shared_host_note: string;
  captured_at: string;
};

export type DatabaseTablesReport = {
  schema_name: string;
  tables: {
    table_name: string;
    estimated_rows: number;
    total_bytes: number;
    table_bytes: number;
    index_bytes: number;
    toast_bytes: number;
    seq_scan: number;
    idx_scan: number;
    n_tup_ins: number;
    n_tup_upd: number;
    n_tup_del: number;
    n_live_tup: number;
    n_dead_tup: number;
    last_vacuum?: string;
    last_autovacuum?: string;
    last_analyze?: string;
    last_autoanalyze?: string;
    dead_tuple_hint?: string;
  }[];
  captured_at: string;
  attribution: string;
};

export type DatabaseLocksReport = {
  blocking: {
    blocked_pid: number;
    blocking_pid: number;
    blocked_query?: string;
    blocking_query?: string;
  }[];
  long_transactions: {
    pid: number;
    state: string;
    xact_start: string;
    age_seconds: number;
    query?: string;
  }[];
  idle_in_transaction: number;
  captured_at: string;
  note: string;
};

export type LogEvent = {
  event_id: string;
  project_id: string;
  request_id?: string;
  service: string;
  severity: string;
  event_name: string;
  event_message: string;
  status_code?: number;
  error_code?: string;
  duration_ms?: number;
  route_template?: string;
  method?: string;
  query_fingerprint?: string;
  created_at: string;
  redaction_version: number;
};

export type LogSearchResult = {
  events: LogEvent[];
  next_cursor?: string;
  has_more: boolean;
  attribution: string;
  from: string;
  to: string;
};

export type SavedLogView = {
  id: string;
  project_id: string;
  name: string;
  filters: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type AuthReport = {
  sign_ins_success: number;
  sign_ins_failed: number;
  sign_ups: number;
  token_refresh_ok: number;
  token_refresh_fail: number;
  sign_outs: number;
  total_auth_requests: number;
  error_rate: number;
  by_route: {
    route_template: string;
    method: string;
    calls: number;
    errors: number;
    error_rate: number;
    avg_duration_ms: number;
  }[];
  time_series: {
    bucket_start: string;
    sign_ins: number;
    sign_ups: number;
    failures: number;
  }[];
  security_events: {
    event_id: string;
    request_id: string;
    route_template: string;
    status_code: number;
    error_code?: string;
    created_at: string;
    kind: string;
  }[];
  from: string;
  to: string;
  attribution: string;
};

export type MCPActivityEvent = {
  id: string;
  event_type: string;
  client_id?: string;
  tool_name?: string;
  result_class: string;
  detail?: string;
  scopes?: string[];
  occurred_at: string;
};

export type MCPToolStat = {
  tool_name: string;
  calls: number;
  success: number;
  denied: number;
  failure: number;
};

export type MCPActivityStats = {
  total_calls: number;
  success_count: number;
  denied_count: number;
  failure_count: number;
  by_tool: MCPToolStat[];
  by_result_class: Record<string, number>;
  from: string;
  to: string;
  attribution: string;
};

export type MCPObservabilityReport = {
  stats: MCPActivityStats;
  events: MCPActivityEvent[];
  from: string;
  to: string;
};

export type ServiceHealth = {
  service: string;
  status: "healthy" | "degraded" | "down" | "unknown";
  rule: string;
  requests: number;
  error_rate: number;
  five_xx_rate: number;
  p95_ms: number;
  last_event_at?: string;
  drill_down: string;
  capability_enabled: boolean;
};

export type OverviewResult = {
  from: string;
  to: string;
  request_volume: number;
  api_error_rate: number;
  api_p95_ms: number;
  slow_queries: number;
  schema_size_bytes: number;
  peak_project_connections: number;
  services: ServiceHealth[];
  data_freshness: string;
  attribution_note: string;
  health_rules: Record<string, number>;
  captured_at: string;
};

export type QuotasReport = {
  project_id: string;
  cycle: string;
  cycle_start: string;
  cycle_end: string;
  counters: {
    api_requests: number;
    auth_requests: number;
    database_queries: number;
    rows_read: number;
    rows_written: number;
    request_bytes: number;
    response_bytes: number;
    error_count: number;
  };
  limits: {
    api_requests: number;
    auth_requests: number;
    database_queries: number;
    egress_bytes: number;
    rows_read: number;
    rows_written: number;
  };
  metrics: {
    name: string;
    used: number;
    limit: number;
    utilization_pct: number;
    soft: boolean;
  }[];
  /** When true, platform hard plan limits reject over-quota /v1/data traffic (US-071). */
  enforced: boolean;
  note: string;
};

export type StorageBucket = {
  id: string;
  project_id: string;
  name: string;
  is_public: boolean;
  created_at: string;
};

export type StorageObjectMeta = {
  id: string;
  bucket_id: string;
  project_id: string;
  path: string;
  content_type: string;
  size_bytes: number;
  owner_user_id?: string | null;
  created_at: string;
  updated_at: string;
};

export type StorageSignedURL = {
  token: string;
  expires_at: string;
};

export type ImageVisibility = "public" | "authenticated" | "owner" | "signed";
export type ImageStatus = "pending" | "processing" | "ready" | "failed";

export type ImageVariant = {
  id: string;
  image_id: string;
  project_id: string;
  tier: string;
  format: string;
  width: number;
  height: number;
  size_bytes: number;
  content_type: string;
  created_at: string;
};

export type ProjectImage = {
  id: string;
  project_id: string;
  filename: string;
  alt_text: string;
  content_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
  visibility: ImageVisibility;
  status: ImageStatus;
  owner_user_id?: string | null;
  focal_x?: number | null;
  focal_y?: number | null;
  error_message?: string | null;
  variants?: ImageVariant[];
  created_at: string;
  updated_at: string;
};

export type ImageJob = {
  id: string;
  project_id: string;
  image_id: string;
  job_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  updated_at: string;
};

export type ImageTransformSpec = {
  crop_x?: number;
  crop_y?: number;
  crop_width?: number;
  crop_height?: number;
  max_edge?: number;
  width?: number;
  height?: number;
  rotate?: number;
  flip_h?: boolean;
  flip_v?: boolean;
  quality?: number;
  formats?: string[];
};

export type ImageSignedURL = {
  token: string;
  expires_at: string;
  path?: string;
};

export type EdgeFunction = {
  id: string;
  project_id: string;
  name: string;
  slug: string;
  description: string;
  status: "draft" | "active" | "disabled";
  active_version?: number | null;
  source?: string;
  source_sha256?: string;
  created_at: string;
  updated_at: string;
};

export type EdgeFunctionSecret = {
  name: string;
  created_at: string;
  updated_at: string;
};

export type EdgeFunctionLog = {
  level: "debug" | "info" | "warn" | "error";
  message: string;
  created_at: string;
};

export type EdgeFunctionInvocation = {
  id: string;
  function_id: string;
  function_slug: string;
  version: number;
  request_id: string;
  outcome: string;
  status_code: number;
  duration_ms: number;
  request_bytes: number;
  response_bytes: number;
  error_message?: string | null;
  invoked_at: string;
  logs: EdgeFunctionLog[];
};

export type EdgeFunctionStats = {
  project_id: string;
  from: string;
  to: string;
  invocations: number;
  errors: number;
  compute_ms: number;
  request_bytes: number;
  response_bytes: number;
  p50_duration_ms: number;
  p95_duration_ms: number;
  monthly_invocation_limit: number;
  monthly_compute_ms_limit: number;
  buckets: Array<{
    bucket_start: string;
    invocations: number;
    errors: number;
    compute_ms: number;
    request_bytes: number;
    response_bytes: number;
  }>;
};

export type EdgeFunctionTestResult = {
  status: number;
  body: string;
  headers: Record<string, string>;
  invocation_id?: string | null;
  version?: string | null;
};

/** Owner impersonation result (US-069). Treat access_token as secret. */
export type ImpersonationResult = {
  access_token: string;
  token_type: string;
  expires_in: number;
  expires_at: string;
  user: AppUser;
  impersonator_id: string;
};

export type AlertRule = {
  id: string;
  project_id: string;
  name: string;
  metric: string;
  operator: string;
  threshold: number;
  window_minutes: number;
  severity: string;
  enabled: boolean;
  cooldown_minutes: number;
  created_at: string;
  updated_at: string;
};

export type AlertIncident = {
  id: string;
  project_id: string;
  rule_id: string;
  status: string;
  severity: string;
  message: string;
  metric_value: number;
  opened_at: string;
  acknowledged_at?: string;
  resolved_at?: string;
  fingerprint: string;
};

export type MetricCatalogEntry = {
  id: string;
  name: string;
  description: string;
  unit: string;
  source: string;
};

export type CustomReport = {
  id: string;
  project_id: string;
  name: string;
  layout: { blocks?: { type: string; metric_id: string }[] };
  created_at: string;
  updated_at: string;
};
